import React from "react";
import Booking from "../../booking/types/Booking";
import useTranslate from "../../core/languages/hooks/useTranslate";
import {formatNumber} from "../../common/utils/number";
import {
    BuildingStorefrontIcon,
    DocumentTextIcon,
    ChevronDownIcon,
    CheckCircleIcon,
    ArrowPathIcon,
    CalendarDaysIcon,
    ReceiptPercentIcon,
} from "@heroicons/react/24/outline";
import useBookingPaymentsGet from "../../booking/hooks/useBookingPaymentsGet";
import PaymentRow from "./PaymentRow";
import Payment from "../types/Payment";
import moment from "moment/moment";
import {ItemRow, TotalRow} from "./SummaryComponents";
import useCurrencyGet from "../hooks/useCurrencyGet";

interface PriceSummaryProps {
    bookingInfo: Booking;
    cityTax: number;
    hidePaymentPending?: boolean;
}

const PriceSummary: React.FC<PriceSummaryProps> = ({bookingInfo, cityTax, hidePaymentPending = false}) => {
    const t = useTranslate();
    const currency = bookingInfo.currencySymbol;
    const bookingInternalId = (bookingInfo as any).internalId || (bookingInfo as any).id;
    const paymentsQuery = useBookingPaymentsGet(bookingInternalId);
    const payments: Payment[] = paymentsQuery.data || [];
    const [pagosOpen, setPagosOpen] = React.useState<boolean>(false);
    const [reembolsosOpen, setReembolsosOpen] = React.useState<boolean>(false);

    const pagos = payments.filter((p) => p.type === 1);
    const reembolsos = payments.filter((p) => p.type !== 1);
    const totalPagos = pagos.reduce((sum, p) => sum + Number(p.originalAmount || p.amount), 0);
    const totalReembolsos = reembolsos.reduce((sum, p) => sum + Number(p.originalAmount || p.amount), 0);
    
    // Encontrar pagos/reembolsos con conversión de moneda
    const pagosConConversion = pagos.find((p) => p.originalCurrency && p.currency && p.originalCurrency !== p.currency);
    const reembolsosConConversion = reembolsos.find((p) => p.originalCurrency && p.currency && p.originalCurrency !== p.currency);
    
    // Obtener símbolo de moneda secundaria (hooks llamados ANTES de early returns)
    const {data: pagosSecondaryCurrencyData} = useCurrencyGet(pagosConConversion?.currency);
    const {data: reembolsosSecondaryCurrencyData} = useCurrencyGet(reembolsosConConversion?.currency);
    const pagosSecondaryCurrencySymbol = pagosSecondaryCurrencyData?.symbol;
    const reembolsosSecondaryCurrencySymbol = reembolsosSecondaryCurrencyData?.symbol;
    
    // Total en moneda secundaria
    const totalPagosOther = pagos.reduce((sum, p) => {
        const hasSecondaryCurrency = Boolean(p.originalCurrency && p.currency && p.originalCurrency !== p.currency);
        return sum + (hasSecondaryCurrency ? Number(p.amount || 0) : 0);
    }, 0);
    const totalReembolsosOther = reembolsos.reduce((sum, p) => {
        const hasSecondaryCurrency = Boolean(p.originalCurrency && p.currency && p.originalCurrency !== p.currency);
        return sum + (hasSecondaryCurrency ? Number(p.amount || 0) : 0);
    }, 0);

    if (bookingInfo.status === "canceled") return null;
    
    const pendingAmount = Math.max(Number(bookingInfo.paymentDifference || 0), 0);
    const scheduledPaymentDate = bookingInfo.paymentScheduledAt
        ? moment(bookingInfo.paymentScheduledAt).format("DD/MM/YYYY")
        : null;
    const pendingSubtitle = scheduledPaymentDate
        ? t("bookOk.remainingPayment", {date: scheduledPaymentDate})
        : t("bookOk.totalPendingSubtitle");

    const renderAmountPair = (primary: number, primarySymbol: string, secondary?: number, secondarySymbol?: string, primaryClassName = "text-lg font-bold", prefix = "") => (
        <div className="flex flex-col items-end">
            <span className={primaryClassName}>{prefix}{formatNumber(primary)} {primarySymbol}</span>
            {secondary !== undefined && secondary > 0 && secondarySymbol && (
                <span className="text-[11px] leading-tight text-neutral-500 dark:text-neutral-400">
                    {formatNumber(secondary)} {secondarySymbol}
                </span>
            )}
        </div>
    );

    return (
        <div className="max-w-full space-y-4">

            <h3 className="text-3xl lg:text-2xl font-semibold mb-2">{t('bookOk.summaryTitle')}</h3>
            <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-6">{t('bookOk.summarySubtitle')}</p>

            {/* ── Desglose: habitaciones + impuestos + total ── */}
            <div className="bg-white dark:bg-neutral-900 border border-neutral-100 dark:border-neutral-800 rounded-2xl divide-y divide-neutral-100 dark:divide-neutral-800 overflow-hidden">

                {/* Habitaciones */}
                <div className="px-4">
                    <ItemRow
                        icon={<BuildingStorefrontIcon className="w-6 h-6 text-indigo-500" />}
                        iconBg="bg-indigo-50 dark:bg-indigo-900/20"
                        title={t('bookOk.totalRooms')}
                        subtitle={t('bookOk.totalRoomsSubtitle')}
                        amount={<span className="text-base font-semibold text-neutral-900 dark:text-neutral-100">{formatNumber(bookingInfo.totalWithoutTaxes)} {currency}</span>}
                    />
                </div>

                {/* Impuestos */}
                <div className="px-4">
                    <ItemRow
                        icon={<ReceiptPercentIcon className="w-6 h-6 text-purple-500" />}
                        iconBg="bg-purple-50 dark:bg-purple-900/20"
                        title={t('bookOk.taxesAndFees') + ` (${cityTax}%)`}
                        subtitle={t('bookOk.taxesSubtitle')}
                        amount={<span className="text-base font-semibold text-neutral-900 dark:text-neutral-100">{formatNumber(bookingInfo.taxes)} {currency}</span>}
                    />
                </div>

                {/* Descuento */}
                {bookingInfo.discountAmount && bookingInfo.discountAmount > 0 && (
                    <div className="px-4">
                        <ItemRow
                            icon={<DocumentTextIcon className="w-6 h-6 text-red-500" />}
                            iconBg="bg-red-50 dark:bg-red-900/20"
                            title={t('bookOk.discount')}
                            subtitle={t('bookOk.taxesSubtitle')}
                            amount={<span className="text-base font-semibold text-neutral-900 dark:text-neutral-100">−{formatNumber(bookingInfo.discountAmount)} {currency}</span>}
                        />
                    </div>
                )}

                {/* Total de la reserva */}
                <TotalRow
                    label={t('bookOk.totalBasket') || 'Total de la reserva'}
                    amount={<>{formatNumber(bookingInfo.totalBasket)} {currency}</>}
                />
            </div>

            {/* ── Pagos y reembolsos (solo si hay movimientos) ── */}
            {(bookingInfo.totalPaid > 0 || pendingAmount > 0 || reembolsos.length > 0) && (
                <div className="bg-white dark:bg-neutral-900 border border-neutral-100 dark:border-neutral-800 rounded-2xl divide-y divide-neutral-100 dark:divide-neutral-800 overflow-hidden">

                    {/* Pagos */}
                    {bookingInfo.totalPaid > 0 && (
                        <div className="px-4">
                            <ItemRow
                                icon={<CheckCircleIcon className="w-6 h-6 text-green-500" />}
                                iconBg="bg-green-50 dark:bg-green-900/20"
                                title={t('bookOk.payments') || 'Pagos'}
                                subtitle={t('bookOk.paymentsSubtitle')}
                                amount={renderAmountPair(
                                    paymentsQuery.isLoading ? bookingInfo.totalPaid : totalPagos,
                                    currency,
                                    paymentsQuery.isLoading ? undefined : totalPagosOther,
                                    pagosSecondaryCurrencySymbol,
                                    "text-lg font-bold text-green-600"
                                )}
                                onClick={() => setPagosOpen((s) => !s)}
                                endElement={<ChevronDownIcon className={`w-5 h-5 text-neutral-400 transition-transform duration-200 ${pagosOpen ? 'rotate-180' : ''}`} />}
                            />
                            {pagosOpen && (
                                <div className="pb-3">
                                    {paymentsQuery.isLoading && <div className="text-xs text-neutral-500 py-2">{t('common.actions.loading') || 'Loading...'}</div>}
                                    {!paymentsQuery.isLoading && pagos.length === 0 && <div className="text-xs text-neutral-500 py-2">{t('bookOk.noPayments') || 'Sin pagos'}</div>}
                                    {pagos.length > 0 && (
                                        <div className="rounded-xl border border-neutral-100 dark:border-neutral-800 overflow-hidden">
                                            {pagos.map((p) => (
                                                <PaymentRow key={p.id} payment={p} currencySymbol={currency} />
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Pendiente de pago */}
                    {pendingAmount > 0 && !hidePaymentPending && (
                        <div className="px-4">
                            <ItemRow
                                icon={<CalendarDaysIcon className="w-6 h-6 text-amber-500" />}
                                iconBg="bg-amber-50 dark:bg-amber-900/20"
                                title={t('bookOk.totalPending')}
                                subtitle={pendingSubtitle}
                                amount={<span className="text-lg font-bold text-amber-600">{formatNumber(pendingAmount)} {currency}</span>}
                            />
                        </div>
                    )}

                    {/* Reembolsos */}
                    {reembolsos.length > 0 && (
                        <div className="px-4">
                            <ItemRow
                                icon={<ArrowPathIcon className="w-6 h-6 text-red-500" />}
                                iconBg="bg-red-50 dark:bg-red-900/20"
                                title={t('bookOk.refunds') || 'Reembolsos'}
                                subtitle={t('bookOk.refundsSubtitle') || 'Importe total reembolsado'}
                                amount={renderAmountPair(
                                    totalReembolsos,
                                    currency,
                                    totalReembolsosOther,
                                    reembolsosSecondaryCurrencySymbol,
                                    "text-lg font-bold text-red-500",
                                    "−"
                                )}
                                onClick={() => setReembolsosOpen((s) => !s)}
                                endElement={<ChevronDownIcon className={`w-5 h-5 text-neutral-400 transition-transform duration-200 ${reembolsosOpen ? 'rotate-180' : ''}`} />}
                            />
                            {reembolsosOpen && (
                                <div className="pb-3">
                                    <div className="rounded-xl border border-neutral-100 dark:border-neutral-800 overflow-hidden">
                                        {reembolsos.map((p) => (
                                            <PaymentRow key={p.id} payment={p} currencySymbol={currency} />
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default PriceSummary;
